/**
 * src/pages/PaywallPage.tsx — Phase 16 ACCESS-05
 *
 * Paywall page: shown by <RequireAuth> when authLoading===false && userId && access==='none'.
 * Provides: Upgrade CTA, conditional Manage-subscription (if hasHadSubscription),
 * logout (T-10-07 order), data-safety reassurance, read-only link.
 *
 * Security notes:
 *   T-16-07: logout order matches SessionWidget exactly (clearQueue → signOut → clearUser).
 *   T-16-04: this is UX only — server requireAccess is the real gate.
 *   T-16-08: data is preserved; sync queue is retained on 402 (useSyncStore unchanged).
 */

import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Clock, ShieldCheck } from 'lucide-react';
import { Logo } from '@/shared/ui/Logo';
import { useAuthStore } from '@/stores/useAuthStore';
import { authClient } from '@/lib/auth-client';
import { clearQueue } from '@/shared/lib/changeQueue';
import { useUpgradeModal, usePortal, IntervalToggle, type BillingInterval } from '@/features/billing';
import { SessionWidget } from '@/features/auth';
import { LanguageSelector } from '@/shared/ui/LanguageSelector';
import Button from '@/shared/ui/button';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';

const PaywallPage: FC = () => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const { clearUser, planStatus, setReadOnlyPreview } = useAuthStore();
  const { error: toastError } = useToast();

  // Checkout + portal hooks
  const { loading: checkoutLoading, error: checkoutError, handleCheckout } = useUpgradeModal();
  const { loading: portalLoading, error: portalError, handlePortal } = usePortal();
  const [interval, setInterval] = useState<BillingInterval>('monthly');

  // Conditional manage-subscription: show only if user has ever had a Stripe subscription
  // planStatus non-null = Stripe has been involved with this user (active, canceled, past_due, etc.)
  const hasHadSubscription = planStatus !== null;

  // T-10-07 logout order: clearQueue → signOut → clearUser (must match SessionWidget exactly)
  const handleLogout = async () => {
    try {
      await clearQueue();
      await authClient.signOut();
      clearUser();
    } catch (err) {
      console.warn('[PaywallPage] logout failed:', err);
      toastError(t('auth.logout_error', 'Не удалось выйти. Попробуйте снова.'));
    }
  };

  // WR-04: Activate soft-lock read-only preview.
  // Sets readOnlyPreview:true in useAuthStore → RequireAuth passes through to children.
  // A sticky ReadOnlyBanner (mounted in App.tsx) communicates the read-only state and
  // provides an upgrade CTA. Server requireAccess still returns 402 on writes (T-16-04).
  const handleViewReadOnly = () => {
    setReadOnlyPreview(true);
  };

  const duration = reduced ? 0 : 0.4;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300 overflow-x-hidden">
      {/* Background decorations — exact copy from StartPage */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-100/50 dark:bg-blue-900/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute top-1/2 -right-24 w-80 h-80 bg-purple-100/50 dark:bg-purple-900/10 rounded-full blur-3xl opacity-50" />
      </div>

      {/* Header — z-30 (above the z-10 main) so the LanguageSelector dropdown, which is
          absolute inside this header's stacking context, renders over the centered card. */}
      <header className="relative h-20 flex items-center justify-between px-6 md:px-12 z-30">
        <Logo className="h-9 w-auto" />
        {/* Right: SessionWidget + LanguageSelector — user is authed */}
        <div className="flex items-center gap-4">
          <SessionWidget />
          <LanguageSelector />
        </div>
      </header>

      {/* Main: single centered column */}
      <main
        className="relative z-10 max-w-lg mx-auto px-6 pt-16 pb-24 flex flex-col gap-8"
        aria-label={t('billing.paywall_main_label', 'Subscription expired')}
      >
        {/* Expired state card */}
        <motion.div
          className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-card p-8 flex flex-col gap-6 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration }}
        >
          {/* Clock icon in amber container */}
          <div className="w-16 h-16 mx-auto bg-amber-50 dark:bg-amber-900/20 rounded-2xl flex items-center justify-center">
            <Clock size={32} className="text-amber-600 dark:text-amber-400" aria-hidden="true" />
          </div>

          {/* Headline + body */}
          <div className="flex flex-col gap-2">
            <h1 className="text-heading text-slate-900 dark:text-slate-100">
              {t('billing.trial_ended_title', 'Trial period ended')}
            </h1>
            <p className="text-body text-slate-500 dark:text-slate-400">
              {t('billing.trial_ended_body', 'Your data is saved. Subscribe to continue working.')}
            </p>
          </div>

          {/* Plan + price block */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 flex flex-col gap-3">
            <span className="text-caption uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {t('billing.current_plan', 'Current plan')} · {t('billing.plan_pro', 'Pro')}
            </span>

            {/* Interval selector — monthly / annual */}
            <IntervalToggle value={interval} onChange={setInterval} disabled={checkoutLoading} />

            {/* Price for the selected interval. The savings line is always rendered
                (fades in for annual) so toggling the interval never shifts the CTA. */}
            <div className="flex flex-col gap-0.5">
              <span className="text-subhead font-bold text-slate-900 dark:text-slate-100">
                {interval === 'annual'
                  ? t('billing.price_annual', '499 PLN/yr')
                  : t('billing.price_monthly', '49 PLN/mo')}
              </span>
              <span
                className={`text-caption font-medium text-green-600 dark:text-green-400 transition-opacity duration-200 ${
                  interval === 'annual' ? 'opacity-100' : 'opacity-0'
                }`}
                aria-hidden={interval !== 'annual'}
              >
                {t('billing.annual_savings', '−15% · ≈1.8 months free')}
              </span>
            </div>
          </div>

          {/* Primary CTA: Upgrade */}
          <Button
            variant="default"
            size="lg"
            className="w-full"
            onClick={() => void handleCheckout(interval)}
            disabled={checkoutLoading}
            aria-busy={checkoutLoading}
          >
            {checkoutLoading
              ? t('billing.checkout_loading', 'Opening checkout...')
              : t('billing.upgrade_cta', 'Upgrade to Pro')}
          </Button>

          {/* Secondary CTA: Manage subscription (only if user has had a subscription) */}
          {hasHadSubscription && (
            <Button
              variant="outline"
              size="default"
              className="w-full"
              onClick={() => void handlePortal()}
              disabled={portalLoading}
              aria-busy={portalLoading}
            >
              {portalLoading
                ? t('billing.manage_loading', 'Opening portal...')
                : t('billing.manage_subscription', 'Manage subscription')}
            </Button>
          )}

          {/* CR-03: checkout and portal errors rendered separately with correct messages */}
          {checkoutError && (
            <p className="text-body text-destructive text-center" role="alert">
              {t('billing.checkout_error', 'Could not open checkout. Please try again.')}
            </p>
          )}
          {portalError && (
            <p className="text-body text-destructive text-center" role="alert">
              {t('billing.portal_error', 'Could not open subscription management. Please try again.')}
            </p>
          )}

          {/* Logout text button */}
          <button
            type="button"
            onClick={() => void handleLogout()}
            className="text-body text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors underline-offset-2 hover:underline"
          >
            {t('auth.logout', 'Выйти')}
          </button>
        </motion.div>

        {/* Data safety reassurance — outside the card */}
        <div className="flex items-center justify-center gap-2 text-caption text-slate-400 dark:text-slate-500">
          <ShieldCheck size={14} className="shrink-0" aria-hidden="true" />
          <span>{t('billing.data_safe', 'All your projects and estimates are saved and accessible after payment.')}</span>
        </div>

        {/* Read-only preview link — soft-lock (plan 16-03 adds the banner, here just navigate) */}
        <button
          type="button"
          onClick={handleViewReadOnly}
          className="text-body text-blue-600 dark:text-blue-400 hover:underline underline-offset-2 text-center"
        >
          {t('billing.view_readonly', 'View projects (read-only)')}
        </button>
      </main>
    </div>
  );
};

export default PaywallPage;
